import "react-checkbox-tree/lib/react-checkbox-tree.css";

import { useEffect, useRef, useState } from "react";

import Dropdown from "shared/components/inputs/tree-select/Dropdown";
import Link from "shared/components/navigation/links/AnchorLink";
import PropTypes from "prop-types";
import { Subtitle } from "../Filter.styles";
import Text from "shared/components/data-display/mui/Text";
import Transform from "features/allocations/api/transform";
import WarningBox from "./WarningBox";
import { useTitles } from "features/allocations/api/useTitles";

const warningMessage =
    "You have adjusted filters. Saved Filters have been cleared.";

let _selectedDepartments = [];
let _selectedTitles = [];

const DepartmentsAndTitles = ({
    onChange,
    savedTitles,
    savedDepartments,
    filterSelected,
    handleSavedFilterChangedWarning,
}) => {
    const [departmentsAndTitles, setDepartmentsAndTitles] = useState([]);
    const [displaySearchCloseIcon, setDisplaySearchCloseIcon] = useState(false);
    const [savedSelection, setSavedSelection] = useState();
    const [showWarning, setShowWarning] = useState(false);
    const { getTitlesFilter } = useTitles();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setSavedSelection({
            departments: savedDepartments,
            titles: savedTitles,
        });
        _selectedDepartments = savedDepartments;
        _selectedTitles = savedTitles;
    }, [savedDepartments, savedTitles]);

    const initialRender = useRef(true);

    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
        } else {
            loadDepartmentsAndTitles(
                savedSelection?.departments,
                savedSelection?.titles
            );
        }
        // loadDepartmentsAndTitles reads departmentsAndTitles state internally —
        // adding it would cause an infinite reload loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedSelection]);

    const getDepartmentsAndTitles = async () => {
        const data = await getTitles();
        
        if (data) {
            return Transform.titles(data);
        }
        return [];
    };

    const handleDepartmentAndTitleChange = async (
        currentNode,
        selectedNodes
    ) => {
        _selectedDepartments = selectedNodes
            .filter((item) => item._depth === 0)
            .map((item) => {
                return { value: item.value, label: item.tagLabel };
            });
        _selectedTitles = selectedNodes
            .filter((item) => item._depth === 1)
            .map((item) => {
                return { value: item.value, label: item.tagLabel };
            });

        if (!currentNode.checked) {
            loadDepartmentsAndTitles(_selectedDepartments, _selectedTitles);
            onChange(_selectedDepartments, _selectedTitles);
        }
    };

    const handleDepartmentAndTitleBlur = async () => {
        setDisplaySearchCloseIcon(false);
        loadDepartmentsAndTitles(_selectedDepartments, _selectedTitles);
        onChange(_selectedDepartments, _selectedTitles);
    };

    const handleDepartmentAndTitleFocus = () => {
        setDisplaySearchCloseIcon(true);
    };

    const handleDepartmentAndTitleClearSearch = (e) => {
        e.preventDefault();
        let element = document.querySelector(".search");
        if (element) {
            element.value = "";
        }
    };

    const handleClear = async () => {
        _selectedDepartments = [];
        _selectedTitles = [];
        loadDepartmentsAndTitles(_selectedDepartments, _selectedTitles);
        onChange(_selectedDepartments, _selectedTitles);
    };

    const mapTitle = (title, departmentChecked, selectedTitles) => ({
        ...title,
        checked: departmentChecked || (selectedTitles?.find((item) => item.value === title.value)),
    });

    const mapDepartment = (department, selectedDepartments, selectedTitles) => {
        const departmentChecked = selectedDepartments?.find((item) => item.value === department.value);
        return {
            ...department,
            checked: departmentChecked,
            children: department.children?.map((title) => mapTitle(title, departmentChecked, selectedTitles)),
        };
    };

    const loadDepartmentsAndTitles = async (
        selectedDepartments,
        selectedTitles
    ) => {
        let data = departmentsAndTitles;
        if (!departmentsAndTitles || departmentsAndTitles?.length === 0)
            data = await getDepartmentsAndTitles();

        const departmentsAndTitlesCopy = data.map((department) => mapDepartment(department, selectedDepartments, selectedTitles));

        if (filterSelected) {
            const validation =
                JSON.stringify(
                    savedSelection?.departments.map(
                        (department) => department.value
                    )
                ) !==
                JSON.stringify(
                    selectedDepartments.map(
                        (department) => department.value
                    )
                ) ||
                JSON.stringify(
                    savedSelection?.titles.map((department) => department.value)
                ) !==
                JSON.stringify(
                    selectedTitles.map((department) => department.value)
                );

            setShowWarning(validation);
        } else {
            setShowWarning(false);
        }

        setDepartmentsAndTitles(departmentsAndTitlesCopy);
    };

    const getTitles = async (departmentIds = null) => {
        const payload = await getTitlesFilter({ departmentid: departmentIds });

        setIsLoading(false);

        if (payload && !payload.error) {
            return payload;
        }
    };

    useEffect(() => {
        if (showWarning) {
            handleSavedFilterChangedWarning(true);
        }
    }, [showWarning, handleSavedFilterChangedWarning]);

    return (
        <>
            <Subtitle>
                <Text variant="medium">Department or Title</Text>

                <Link
                    onClick={() => handleClear()}
                    style={{ marginLeft: "1rem", paddingTop: "4px" }}
                    disabled={
                        isLoading ||
                        (_selectedDepartments.length === 0 &&
                            _selectedTitles.length === 0)
                    }
                >
                    Clear
                </Link>
            </Subtitle>

            <Dropdown
                data={departmentsAndTitles}
                placeholder="Search for a Department or Title"
                searchLabel="Search for a Department or Title"
                mode="multiSelect"
                inlineSearch={true}
                showPartial={true}
                displaySearchCloseIcon={displaySearchCloseIcon}
                handleOnClickClearSearchFieldData={
                    handleDepartmentAndTitleClearSearch
                }
                onChange={(currentNode, selectedNodes) =>
                    handleDepartmentAndTitleChange(currentNode, selectedNodes)
                }
                onBlur={() => handleDepartmentAndTitleBlur()}
                onFocus={() => handleDepartmentAndTitleFocus()}
                disabled={
                    !departmentsAndTitles || departmentsAndTitles.length === 0
                }
                className="dropdown-custom-content departmenttitledd"
            />

            {showWarning && <WarningBox>{warningMessage}</WarningBox>}
        </>
    );
};
DepartmentsAndTitles.propTypes = {
    onChange: PropTypes.func,
    savedTitles: PropTypes.array,
    savedDepartments: PropTypes.array,
    filterSelected: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    handleSavedFilterChangedWarning: PropTypes.func,
};

export default DepartmentsAndTitles;
